import React, { useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { Trans, useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.observability';
import { getEnvironment } from '@minecraft.environment';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import {
  CopyToCastItStatusEnum,
  CopyToCastItResultEnum,
  GetCopyToCastItJobStatusDoc,
} from '@minecraft.graphql-operations';
import { Link } from '@minecraft.atoms';
import { PollingHandler, PollingInstanceStatus } from './types';

/**
 * Hook that handles polling for CopyToCastIt job statuses.
 * Should be used once at the app level, inside the PollingProvider.
 */
export const useCopyToCastItPolling = () => {
  const apollo = useApolloClient();

  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();

  const showSuccessMessage = useCallback(() => {
    showMessage({
      message: (
        <Trans
          i18nKey="casting:copyToCastIt.copySuccess"
          components={[<Link target="_blank" to={getEnvironment().CASTIT_URL} />]}
        />
      ),
      color: 'success',
    });
    captureHeapEvent({ name: HEAP_EVENT_NAMES.COPY_TO_CAST_IT_SUCCESS });
  }, [showMessage]);

  const showErrorMessage = useCallback(() => {
    showMessage({
      message: t('casting:copyToCastIt.copyError.noneCopied'),
      color: 'danger',
    });
    captureHeapEvent({ name: HEAP_EVENT_NAMES.COPY_TO_CAST_IT_FAILED_TO_COPY });
  }, [showMessage, t]);

  const pollCopyToCastIt: PollingHandler = useCallback(
    async ({ instance }) => {
      try {
        const res = await apollo.query({
          query: GetCopyToCastItJobStatusDoc,
          variables: {
            input: { jobId: instance.id },
          },
          fetchPolicy: 'no-cache',
        });

        if (!res.data?.getCopyToCastItJobStatus) {
          showErrorMessage();
          return { ...instance, status: PollingInstanceStatus.FAILED };
        }

        const jobStatus = res.data?.getCopyToCastItJobStatus.status;
        const jobResult = res.data?.getCopyToCastItJobStatus.result;
        const jobStatusValue = String(jobStatus ?? '');
        const jobResultValue = String(jobResult ?? '');

        const isStatusInProgress =
          jobStatusValue === CopyToCastItStatusEnum.InProgress || jobStatusValue === 'InProgress';
        const isStatusSuccess = jobStatusValue === CopyToCastItStatusEnum.Success || jobStatusValue === 'Completed';
        const isStatusFailure =
          jobStatusValue === CopyToCastItStatusEnum.Failed || jobStatusValue === CopyToCastItStatusEnum.Cancelled;

        const isResultInProgress =
          jobResultValue === CopyToCastItResultEnum.InProgress || jobResultValue === 'InProgress';
        const isResultFullSuccess =
          jobResultValue === CopyToCastItResultEnum.AllCopied ||
          jobResultValue === CopyToCastItResultEnum.AllAlreadyCopied ||
          jobResultValue === 'Success';
        const isResultPartialSuccess =
          jobResultValue === CopyToCastItResultEnum.PartialCopied ||
          jobResultValue === CopyToCastItResultEnum.PartialAlreadyCopied;
        const isResultFailure =
          jobResultValue === CopyToCastItResultEnum.Error ||
          jobResultValue === CopyToCastItResultEnum.NoneCopied ||
          jobResultValue === CopyToCastItResultEnum.Cancelled;

        if (isResultInProgress || isStatusInProgress) {
          return { ...instance, status: PollingInstanceStatus.IN_PROGRESS };
        }

        if (isResultPartialSuccess) {
          showMessage({
            message: (
              <Trans
                i18nKey="casting:copyToCastIt.copyError.partialCopied"
                components={[<Link target="_blank" to={getEnvironment().CASTIT_URL} />]}
              />
            ),
            color: 'danger',
          });
          return { ...instance, status: PollingInstanceStatus.PARTIAL_SUCCESS };
        }

        if (isResultFullSuccess || isStatusSuccess) {
          showSuccessMessage();
          return { ...instance, status: PollingInstanceStatus.COMPLETED };
        }

        if (isResultFailure || isStatusFailure) {
          showErrorMessage();
          return { ...instance, status: PollingInstanceStatus.FAILED };
        }

        return { ...instance, status: PollingInstanceStatus.IN_PROGRESS };
      } catch (error) {
        showErrorMessage();
        captureExceptionEvent(error, {
          location: 'useCopyToCastItPolling - pollCopyToCastIt',
        });
        return { ...instance, status: PollingInstanceStatus.FAILED };
      }
    },
    [showSuccessMessage, showErrorMessage, apollo]
  );

  return useMemo(() => ({ pollCopyToCastIt }), [pollCopyToCastIt]);
};
